import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import './Toast.scss';
import { errorText } from '../utils/errorText';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  count: number;
  status: 'entering' | 'idle' | 'closing';
  isPulsing?: boolean;
  /** Absolute timestamp (ms) at which this toast should auto-dismiss */
  expiresAt?: number;
  /** Bumped on duplicate promote to remount the progress bar at full duration */
  progressKey: number;
}

/**
 * Global dispatcher — any component can fire toasts via CustomEvent.
 * Hata bildiriminde sunucu kodu (`duplicate_code`) ya da teknik metin
 * ("Failed to fetch") okunur cümleye çevrilir (owner 2026-10-15).
 */
export const showAppToast = (
  message: string,
  type: ToastType = 'success',
  duration = 4000
) => {
  if (typeof window !== 'undefined') {
    const text = type === 'error' && message ? errorText(message) : message;
    window.dispatchEvent(
      new CustomEvent('app-toast', {
        detail: { message: text, type, duration, timestamp: Date.now() },
      })
    );
  }
};

/** @deprecated Use showAppToast */
export const showToast = showAppToast;

const MAX_STACK_SIZE = 5;

/**
 * App-root toast host. Mount once as `<Toast />` in App; listen only to
 * `app-toast` events from `showAppToast`. No prop-driven / local mounts.
 */
const Toast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const lastTriggerRef = useRef<{ key: string; time: number }>({
    key: '',
    time: 0,
  });
  /** Timestamp when the user started hovering — used to extend expiresAt on leave */
  const hoveredAtRef = useRef<number | null>(null);

  const updateHeight = useCallback((id: string, h: number) => {
    setHeights((prev) => {
      if (prev[id] === h) return prev;
      return { ...prev, [id]: h };
    });
  }, []);

  const addOrUpdateToast = useCallback(
    (msg: string, toastType: ToastType = 'success', toastDuration = 4000) => {
      if (!msg) return;

      const now = Date.now();
      const triggerKey = `${toastType}:${msg}`;

      // Deduplicate identical triggers within 250ms (double-fire guards)
      if (
        lastTriggerRef.current.key === triggerKey &&
        now - lastTriggerRef.current.time < 250
      ) {
        return;
      }
      lastTriggerRef.current = { key: triggerKey, time: now };

      setToasts((prevToasts) => {
        const existingIndex = prevToasts.findIndex(
          (t) =>
            t.message === msg && t.type === toastType && t.status !== 'closing'
        );

        if (existingIndex !== -1) {
          // Same action -> increment pill, pulse, reset timer, move to front
          const existing = prevToasts[existingIndex];
          const promoted: ToastItem = {
            ...existing,
            count: existing.count + 1,
            isPulsing: true,
            status: 'idle',
            expiresAt: now + toastDuration,
            progressKey: existing.progressKey + 1,
          };
          const rest = prevToasts.filter((_, idx) => idx !== existingIndex);
          return [promoted, ...rest].slice(0, MAX_STACK_SIZE);
        }

        const newToast: ToastItem = {
          id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message: msg,
          type: toastType,
          duration: toastDuration,
          count: 1,
          progressKey: 0,
          status: 'entering',
        };

        return [newToast, ...prevToasts].slice(0, MAX_STACK_SIZE);
      });
    },
    []
  );

  // Clear pulse after 300ms
  useEffect(() => {
    const hasPulsing = toasts.some((t) => t.isPulsing);
    if (!hasPulsing) return;

    const timer = setTimeout(() => {
      setToasts((prev) =>
        prev.map((t) => (t.isPulsing ? { ...t, isPulsing: false } : t))
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [toasts]);

  // Global app-toast listener (sole input path)
  useEffect(() => {
    const handleGlobalToast = (e: Event) => {
      const customEvent = e as CustomEvent<{
        message: string;
        type?: ToastType;
        duration?: number;
      }>;
      if (customEvent.detail?.message) {
        addOrUpdateToast(
          customEvent.detail.message,
          customEvent.detail.type || 'success',
          customEvent.detail.duration || 4000
        );
      }
    };

    window.addEventListener('app-toast', handleGlobalToast);
    return () => window.removeEventListener('app-toast', handleGlobalToast);
  }, [addOrUpdateToast]);

  // Per-toast auto-dismiss (pauses while hovered); absolute expiry so new
  // toasts never reset timers already in flight.
  useEffect(() => {
    if (isHovered) return;

    const timers = toasts.map((t) => {
      if (t.status !== 'idle' || t.duration <= 0 || !t.expiresAt) return null;
      const remaining = Math.max(0, t.expiresAt - Date.now());
      return setTimeout(() => {
        setToasts((prev) =>
          prev.map((item) =>
            item.id === t.id ? { ...item, status: 'closing' } : item
          )
        );
      }, remaining);
    });

    return () => {
      timers.forEach((timer) => timer && clearTimeout(timer));
    };
  }, [toasts, isHovered]);

  const handleAnimationEnd = (
    e: React.AnimationEvent<HTMLDivElement>,
    toastId: string,
    currentStatus: 'entering' | 'idle' | 'closing'
  ) => {
    if (e.target !== e.currentTarget) return;

    if (currentStatus === 'entering') {
      setToasts((prev) =>
        prev.map((t) =>
          t.id === toastId
            ? {
                ...t,
                status: 'idle',
                expiresAt: Date.now() + t.duration,
              }
            : t
        )
      );
    } else if (currentStatus === 'closing') {
      setToasts((prev) => prev.filter((t) => t.id !== toastId));
      setHeights((prev) => {
        if (!(toastId in prev)) return prev;
        const { [toastId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleManualClose = (toastId: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === toastId ? { ...t, status: 'closing' } : t))
    );
  };

  const getExpandedOffsetY = (targetIndex: number) => {
    const GAP = 10;
    let offset = 0;
    for (let i = 0; i < targetIndex; i++) {
      const tId = toasts[i]?.id;
      const h = (tId && heights[tId]) || 62;
      offset += h + GAP;
    }
    return offset;
  };

  const totalExpandedHeight = useMemo(() => {
    const GAP = 10;
    return toasts.reduce((acc, t, idx) => {
      const h = heights[t.id] || 62;
      return acc + h + (idx > 0 ? GAP : 0);
    }, 0);
  }, [toasts, heights]);

  const frontHeight =
    (toasts[0] ? heights[toasts[0].id] || 62 : 62) +
    (toasts.length > 1 ? 26 : 0);

  // Stable id order so promote only updates transforms/z-index — React must
  // not reorder DOM nodes or CSS progress animations restart/glitch.
  const renderToasts = useMemo(
    () => [...toasts].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
    [toasts]
  );

  const stackIndexById = useMemo(() => {
    const map: Record<string, number> = {};
    toasts.forEach((t, i) => {
      map[t.id] = i;
    });
    return map;
  }, [toasts]);

  if (toasts.length === 0) return null;

  const renderIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 size={18} strokeWidth={2.2} />;
      case 'error':
        return <AlertCircle size={18} strokeWidth={2.2} />;
      case 'warning':
        return <AlertTriangle size={18} strokeWidth={2.2} />;
      case 'info':
        return <Info size={18} strokeWidth={2.2} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`toast-stack ${isHovered ? 'toast-stack--expanded' : 'toast-stack--collapsed'}`}
      style={{
        height: isHovered
          ? Math.max(totalExpandedHeight, 64)
          : Math.max(frontHeight, 64),
      }}
      onMouseEnter={() => {
        hoveredAtRef.current = Date.now();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        const pausedAt = hoveredAtRef.current;
        hoveredAtRef.current = null;
        if (pausedAt !== null) {
          const elapsed = Date.now() - pausedAt;
          setToasts((prev) =>
            prev.map((t) => {
              if (t.status === 'idle' && t.expiresAt != null) {
                return { ...t, expiresAt: t.expiresAt + elapsed };
              }
              return t;
            })
          );
        }
        setIsHovered(false);
      }}
      role="region"
      aria-label="Notifications"
    >
      {renderToasts.map((toast) => {
        const index = stackIndexById[toast.id] ?? 0;
        const isFront = index === 0;
        const isSecond = index === 1;
        const isThird = index === 2;

        const animationClass =
          toast.status === 'entering'
            ? 'toast--entering'
            : toast.status === 'closing'
              ? 'toast--closing'
              : 'toast--idle';
        const pulseClass = toast.isPulsing ? 'toast--pulse' : '';

        const collapsedY = isFront ? 0 : isSecond ? 12 : isThird ? 24 : 24;
        const collapsedScale = isFront
          ? 1
          : isSecond
            ? 0.96
            : isThird
              ? 0.92
              : 0.88;
        const collapsedOpacity = isFront
          ? 1
          : isSecond
            ? 0.85
            : isThird
              ? 0.6
              : 0;
        const expandedY = getExpandedOffsetY(index);

        const stackStyle: React.CSSProperties = isHovered
          ? {
              transform: `translate3d(0, ${expandedY}px, 0) scale(1)`,
              opacity: 1,
              zIndex: MAX_STACK_SIZE - index,
              pointerEvents: 'auto',
            }
          : {
              transform: `translate3d(0, ${collapsedY}px, 0) scale(${collapsedScale})`,
              opacity: collapsedOpacity,
              zIndex: MAX_STACK_SIZE - index,
              pointerEvents: isFront ? 'auto' : 'none',
            };

        return (
          <div
            key={toast.id}
            ref={(el) => {
              if (el) {
                const h = el.offsetHeight;
                if (h > 0) updateHeight(toast.id, h);
              }
            }}
            className={`toast toast-${toast.type} ${animationClass} ${pulseClass}`}
            style={stackStyle}
            role="alert"
            aria-live="polite"
            onAnimationEnd={(e) =>
              handleAnimationEnd(e, toast.id, toast.status)
            }
          >
            <div className="toast-icon">{renderIcon(toast.type)}</div>
            <div className="toast-content">
              <p className="toast-message">
                {toast.message}
                {toast.count > 1 && (
                  <span className="toast-count-badge" key={toast.count}>
                    ×{toast.count}
                  </span>
                )}
              </p>
            </div>
            <button
              className="toast-close"
              onClick={() => handleManualClose(toast.id)}
              aria-label="Close notification"
            >
              <X size={15} strokeWidth={2} />
            </button>

            {toast.duration > 0 && (
              <div className="toast-progress-track">
                <div
                  key={toast.progressKey}
                  className={`toast-progress-bar ${
                    toast.status === 'entering'
                      ? 'toast-progress-bar--waiting'
                      : ''
                  }`}
                  style={{
                    animationDuration: `${toast.duration}ms`,
                    animationPlayState: isHovered ? 'paused' : 'running',
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Toast;
